import cloneDeep from 'clone-deep'
import { SettingKey } from 'config'
import {
  AiEngine,
  AiEngines,
  DEFAULT_AI_ENGINE,
  DEFAULT_AI_MODEL,
  getAiModelsForEngine,
} from 'config/ai.type'
import { useEffect, useState } from 'react'
import { setAdminConfig } from 'services'
import { ToggleButton } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { ILoanGlobalSettings } from './interface'

interface IProps {
  globalSetting: ILoanGlobalSettings | undefined
  setAction: (value: string) => void
  setGlobalSetting: (value: ILoanGlobalSettings) => void
}

export const AiConfiguration = (props: IProps) => {
  const { globalSetting, setGlobalSetting, setAction } = props

  const [engine, setEngine] = useState<AiEngine>(DEFAULT_AI_ENGINE)
  const [model, setModel] = useState(DEFAULT_AI_MODEL)
  const [invoiceAiAutofillEnabled, setInvoiceAiAutofillEnabled] = useState(false)
  const [feeSheetAiAutofillEnabled, setFeeSheetAiAutofillEnabled] = useState(false)
  const [budgetAiAutofillEnabled, setBudgetAiAutofillEnabled] = useState(false)
  const [partyAiAutofillEnabled, setPartyAiAutofillEnabled] = useState(false)
  const [insuranceAiAutofillEnabled, setInsuranceAiAutofillEnabled] = useState(false)
  const [borrowerAiAutofillEnabled, setBorrowerAiAutofillEnabled] = useState(false)
  const [creditScoreAiAutofillEnabled, setCreditScoreAiAutofillEnabled] = useState(false)
  const [secondaryMarketingAiAutofillEnabled, setSecondaryMarketingAiAutofillEnabled] = useState(false)

  useEffect(() => {
    if (!globalSetting) return
    setEngine(globalSetting.defaultAiEngine || DEFAULT_AI_ENGINE)
    setModel(globalSetting.defaultAiModel || DEFAULT_AI_MODEL)
    setInvoiceAiAutofillEnabled(!!globalSetting.invoiceAiAutofillEnabled)
    setFeeSheetAiAutofillEnabled(!!globalSetting.feeSheetAiAutofillEnabled)
    setBudgetAiAutofillEnabled(!!globalSetting.budgetAiAutofillEnabled)
    setPartyAiAutofillEnabled(!!globalSetting.partyAiAutofillEnabled)
    setInsuranceAiAutofillEnabled(!!globalSetting.insuranceAiAutofillEnabled)
    setBorrowerAiAutofillEnabled(!!globalSetting.borrowerAiAutofillEnabled)
    setCreditScoreAiAutofillEnabled(!!globalSetting.creditScoreAiAutofillEnabled)
    setSecondaryMarketingAiAutofillEnabled(!!globalSetting.secondaryMarketingAiAutofillEnabled)
  }, [globalSetting])

  const saveSettings = async (updates: Partial<ILoanGlobalSettings>) => {
    if (!globalSetting) return
    const newSettings = cloneDeep(globalSetting)
    Object.assign(newSettings, updates)
    setAction('setting')
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSettings)
    setGlobalSetting(newSettings)
    setAction('')
  }

  const onEngineChange = async (_key: string, value: AiEngine) => {
    if (!globalSetting) return
    const models = getAiModelsForEngine(value)
    const nextModel = models[model] ? model : Object.keys(models)[0] || DEFAULT_AI_MODEL

    setEngine(value)
    setModel(nextModel)

    await saveSettings({
      defaultAiEngine: value,
      defaultAiModel: nextModel,
    })
  }

  const onModelChange = async (_key: string, value: string) => {
    if (!globalSetting) return
    setModel(value)

    await saveSettings({
      defaultAiEngine: engine,
      defaultAiModel: value,
    })
  }

  const onInvoiceAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setInvoiceAiAutofillEnabled(value)
    await saveSettings({ invoiceAiAutofillEnabled: value })
  }

  const onFeeSheetAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setFeeSheetAiAutofillEnabled(value)
    await saveSettings({ feeSheetAiAutofillEnabled: value })
  }

  const onBudgetAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setBudgetAiAutofillEnabled(value)
    await saveSettings({ budgetAiAutofillEnabled: value })
  }

  const onPartyAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setPartyAiAutofillEnabled(value)
    await saveSettings({ partyAiAutofillEnabled: value })
  }

  const onInsuranceAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setInsuranceAiAutofillEnabled(value)
    await saveSettings({ insuranceAiAutofillEnabled: value })
  }

  const onBorrowerAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setBorrowerAiAutofillEnabled(value)
    await saveSettings({ borrowerAiAutofillEnabled: value })
  }

  const onCreditScoreAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setCreditScoreAiAutofillEnabled(value)
    await saveSettings({ creditScoreAiAutofillEnabled: value })
  }

  const onSecondaryMarketingAiAutofillChange = async (value: boolean) => {
    if (!globalSetting) return
    setSecondaryMarketingAiAutofillEnabled(value)
    await saveSettings({ secondaryMarketingAiAutofillEnabled: value })
  }

  return (
    <div className="relative">
      <div className="input md:col-span-full mb-4">
        <RenderInput
          input={{ inputType: 'section', title: 'AI Configuration' }}
          Key="aiConfigurationSection"
          onChange={() => {}}
        />
      </div>

      <div className="max-w-md flex flex-col gap-4">
        <RenderInput
          input={{
            inputType: 'select',
            title: 'Default AI Engine',
            options: AiEngines,
            hasDefaultOption: false,
            value: engine,
          }}
          Key="defaultAiEngine"
          onChange={onEngineChange}
        />
        <RenderInput
          input={{
            inputType: 'select',
            title: 'Default AI Model',
            options: getAiModelsForEngine(engine),
            hasDefaultOption: false,
            value: model,
          }}
          Key="defaultAiModel"
          onChange={onModelChange}
        />
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Invoice AI Autofill</span>
          <ToggleButton
            id="invoiceAiAutofillEnabled"
            value={invoiceAiAutofillEnabled}
            onChange={onInvoiceAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Fee Sheet AI Autofill (HUD1 / Fee Worksheet)</span>
          <ToggleButton
            id="feeSheetAiAutofillEnabled"
            value={feeSheetAiAutofillEnabled}
            onChange={onFeeSheetAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Budget AI Autofill (Budget & Scope of Work)</span>
          <ToggleButton
            id="budgetAiAutofillEnabled"
            value={budgetAiAutofillEnabled}
            onChange={onBudgetAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Party AI Autofill (Parties)</span>
          <ToggleButton
            id="partyAiAutofillEnabled"
            value={partyAiAutofillEnabled}
            onChange={onPartyAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Insurance AI Autofill (Servicing Information)</span>
          <ToggleButton
            id="insuranceAiAutofillEnabled"
            value={insuranceAiAutofillEnabled}
            onChange={onInsuranceAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Borrower AI Autofill (Borrower Details)</span>
          <ToggleButton
            id="borrowerAiAutofillEnabled"
            value={borrowerAiAutofillEnabled}
            onChange={onBorrowerAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Credit Score AI Autofill (Manual Credit Report)</span>
          <ToggleButton
            id="creditScoreAiAutofillEnabled"
            value={creditScoreAiAutofillEnabled}
            onChange={onCreditScoreAiAutofillChange}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-700">Secondary Marketing AI Autofill (Originator Lock Info)</span>
          <ToggleButton
            id="secondaryMarketingAiAutofillEnabled"
            value={secondaryMarketingAiAutofillEnabled}
            onChange={onSecondaryMarketingAiAutofillChange}
          />
        </div>
      </div>
    </div>
  )
}
